import type { VariableColorVal } from "@tamagui/web";

import { colors } from "./tokens";

type DefaultTamaguiThemeTokens = {
  background: VariableColorVal;
  backgroundFocus: VariableColorVal;
  backgroundHover: VariableColorVal;
  backgroundPress: VariableColorVal;
  backgroundStrong: VariableColorVal;
  backgroundTransparent: VariableColorVal;
  borderColor: VariableColorVal;
  borderColorFocus: VariableColorVal;
  borderColorHover: VariableColorVal;
  borderColorPress: VariableColorVal;
  color: VariableColorVal;
  colorFocus: VariableColorVal;
  colorHover: VariableColorVal;
  colorPress: VariableColorVal;
  colorTransparent: VariableColorVal;
  outlineColor: VariableColorVal;
  placeholderColor: VariableColorVal;
  selectionColor: VariableColorVal;
  shadowColor: VariableColorVal;
  shadowColorFocus: VariableColorVal;
  shadowColorHover: VariableColorVal;
  shadowColorPress: VariableColorVal;
};

type SuperclinicalTheme = DefaultTamaguiThemeTokens & {
  backgroundFill: VariableColorVal;
  backgroundGround: VariableColorVal;
  textPrimary: VariableColorVal;
  textSecondary: VariableColorVal;
  textTertiary: VariableColorVal;
  textDisabled: VariableColorVal;
  textLink: VariableColorVal;
  textLinkHover: VariableColorVal;
  textPatient: VariableColorVal;
  textButtonPrimary: VariableColorVal; // Eng added
  textPrompt: VariableColorVal; // Eng added
  fillPatient: VariableColorVal;
  fillModal: VariableColorVal;
  fillInput: VariableColorVal;
  fillNeutral: VariableColorVal;
  fillCallout: VariableColorVal;
  fillClinician: VariableColorVal;
  fillCuraiSubtle: VariableColorVal;
  fillHeroGradientStart: VariableColorVal;
  fillHeroGradientEnd: VariableColorVal;
  fillButtonPrimary: VariableColorVal;
  fillButtonNeutral: VariableColorVal;
  fillButtonDisabled: VariableColorVal;
  textButtonDisabled: VariableColorVal;
  strokeButtonDisabled: VariableColorVal;
  strokeUnselected: VariableColorVal;
  fillPrompt: VariableColorVal;
  fillPrimary: VariableColorVal;
  shadow: VariableColorVal;
  shadowButton: VariableColorVal;
  shadowPartnerHero: VariableColorVal;
  buttonHighlight: VariableColorVal;
  iconBase: VariableColorVal;
  iconSecondary: VariableColorVal;
  iconCta: VariableColorVal;
  navHover: VariableColorVal;
  navSelected: VariableColorVal;
  strokeSubdued: VariableColorVal;
  strokeNeutral: VariableColorVal;
  strokeButtonSecondary: VariableColorVal;
  strokePopover: VariableColorVal;
  strokePrompt: VariableColorVal;
  fillOverlay: VariableColorVal;
  red: VariableColorVal;
  yellow: VariableColorVal;
  green: VariableColorVal;
  a11yBlue: VariableColorVal;
  fillCallTray: VariableColorVal;
  fillCallControl: VariableColorVal;
  fillCallDanger: VariableColorVal;
  fillCallOverlay: VariableColorVal;
  textOnCall: VariableColorVal;
  contentLoaderBackground: VariableColorVal;
  contentLoaderForeground: VariableColorVal;
  tileBackground: VariableColorVal;
  fillBadgePrimary: VariableColorVal;
  fillBadgeCurai: VariableColorVal;
  fillBadgeTertiary: VariableColorVal;
};

const light: SuperclinicalTheme = {
  background: colors.neutral0,
  backgroundFocus: colors.neutral0,
  backgroundHover: colors.neutral0,
  backgroundPress: colors.neutral0,
  backgroundStrong: colors.neutral0,
  backgroundTransparent: colors.transparent,
  borderColor: colors.neutral900,
  borderColorFocus: colors.neutral900,
  borderColorHover: colors.neutral900,
  borderColorPress: colors.neutral900,
  color: colors.light800,
  colorFocus: colors.light800,
  colorHover: colors.light800,
  colorPress: colors.light800,
  colorTransparent: colors.transparent,
  outlineColor: colors.light_a11yBlue,
  placeholderColor: colors.neutral400,
  selectionColor: colors.curai100,
  shadowColor: colors.light300,
  shadowColorFocus: colors.light300,
  shadowColorHover: colors.light300,
  shadowColorPress: colors.light300,

  // Design System tokens
  backgroundFill: colors.neutral25,
  backgroundGround: colors.neutral0,
  textPrimary: colors.neutral700,
  textSecondary: colors.neutral600,
  textTertiary: colors.neutral500,
  textDisabled: colors.light400,
  textLink: colors.curai500,
  textLinkHover: colors.curai700,
  textPatient: colors.neutral700,
  textButtonPrimary: colors.neutral0,
  textPrompt: colors.neutral700,
  fillModal: colors.neutral25,
  fillPatient: colors.neutral200,
  fillInput: colors.neutral0,
  fillNeutral: colors.light300,
  fillCallout: colors.neutral0,
  fillClinician: colors.curai50,
  fillCuraiSubtle: colors.curai100,
  fillHeroGradientStart: colors.neutral25,
  fillHeroGradientEnd: colors.curai100,
  fillButtonPrimary: colors.curai500,
  fillButtonNeutral: colors.neutral700,
  fillButtonDisabled: colors.neutral200,
  textButtonDisabled: colors.neutral500,
  strokeButtonDisabled: colors.neutral300,
  strokeUnselected: colors.neutral500,
  fillPrompt: colors.curai50,
  fillPrimary: colors.curai500,
  shadow: colors.light300,
  shadowButton: colors.curai500,
  shadowPartnerHero: colors.curai500,
  buttonHighlight: colors.curai500,
  iconBase: colors.neutral700,
  iconSecondary: colors.neutral600,
  iconCta: colors.neutral50,
  navHover: colors.curai50,
  navSelected: colors.curai50,
  strokeSubdued: colors.neutral200,
  strokeNeutral: colors.neutral300,
  strokeButtonSecondary: colors.curai500,
  strokePopover: colors.neutral50,
  strokePrompt: colors.curai300,
  fillOverlay: colors.light400_transparent,
  red: colors.light_red,
  yellow: colors.light_yellow,
  green: colors.light_green,
  a11yBlue: colors.light_a11yBlue,
  // Video call UI sits on live video, so it stays dark in both themes
  fillCallTray: colors.neutral1000,
  fillCallControl: colors.neutral700,
  fillCallDanger: colors.dark_red,
  fillCallOverlay: colors.neutral0_transparent_20,
  textOnCall: colors.neutral0,
  contentLoaderBackground: colors.neutral100,
  contentLoaderForeground: colors.neutral200,
  tileBackground: colors.neutral25,
  fillBadgePrimary: colors.secondary100,
  fillBadgeCurai: colors.curai100,
  fillBadgeTertiary: colors.tertiary100,
};

const dark: SuperclinicalTheme = {
  // Default tamagui tokens
  background: colors.neutral1000,
  backgroundFocus: colors.neutral0,
  backgroundHover: colors.neutral0,
  backgroundPress: colors.neutral0,
  backgroundStrong: colors.neutral0,
  backgroundTransparent: colors.transparent,
  borderColor: colors.neutral900,
  borderColorFocus: colors.neutral900,
  borderColorHover: colors.neutral900,
  borderColorPress: colors.neutral900,
  color: colors.neutral300,
  colorFocus: colors.neutral300,
  colorHover: colors.neutral300,
  colorPress: colors.neutral300,
  colorTransparent: colors.transparent,
  outlineColor: colors.dark_a11yBlue,
  placeholderColor: colors.neutral600,
  selectionColor: colors.curai700,
  shadowColor: "transparent",
  shadowColorFocus: "transparent",
  shadowColorHover: "transparent",
  shadowColorPress: "transparent",

  // Design System tokens
  backgroundFill: colors.neutral900,
  backgroundGround: colors.neutral1000,
  textPrimary: colors.neutral300,
  textSecondary: colors.neutral400,
  textTertiary: colors.neutral500,
  textDisabled: colors.neutral600,
  textLink: colors.curai300,
  textLinkHover: colors.curai100,
  textPatient: colors.neutral700,
  textButtonPrimary: colors.neutral0,
  textPrompt: colors.neutral900,
  fillPatient: colors.neutral300,
  fillModal: colors.neutral900,
  fillInput: colors.neutral900,
  fillNeutral: colors.neutral800,
  fillCallout: colors.neutral800,
  fillClinician: colors.curai700,
  fillCuraiSubtle: colors.curai800,
  fillHeroGradientStart: colors.dark800,
  fillHeroGradientEnd: colors.curai400,
  fillButtonPrimary: colors.curai500,
  fillButtonNeutral: colors.neutral400,
  fillButtonDisabled: colors.neutral800,
  textButtonDisabled: colors.neutral500,
  strokeButtonDisabled: colors.neutral700,
  strokeUnselected: colors.neutral600,
  fillPrompt: colors.curai300,
  fillPrimary: colors.curai400,
  shadow: colors.dark800,
  shadowButton: "transparent",
  // Partner GLP-1 pages keep the brand glow in Dark
  shadowPartnerHero: colors.curai500,
  buttonHighlight: colors.curai500,
  iconBase: colors.neutral200,
  iconSecondary: colors.neutral400,
  iconCta: colors.neutral0,
  navHover: colors.curai800,
  navSelected: colors.curai800,
  strokeSubdued: colors.neutral700,
  strokeNeutral: colors.neutral600,
  strokeButtonSecondary: colors.curai300,
  strokePopover: colors.neutral900,
  strokePrompt: colors.curai100,
  fillOverlay: colors.neutral700_transparent,
  red: colors.dark_red,
  yellow: colors.dark_yellow,
  green: colors.dark_green,
  a11yBlue: colors.dark_a11yBlue,
  // Video call UI sits on live video, so it stays dark in both themes
  fillCallTray: colors.neutral1000,
  fillCallControl: colors.neutral700,
  fillCallDanger: colors.dark_red,
  fillCallOverlay: colors.neutral0_transparent_20,
  textOnCall: colors.neutral0,
  contentLoaderBackground: colors.neutral850,
  contentLoaderForeground: colors.neutral900,
  tileBackground: colors.neutral850,
  fillBadgePrimary: colors.secondary800,
  fillBadgeCurai: colors.curai700,
  fillBadgeTertiary: colors.tertiary800,
};

// clinic subtheme marks the patient's clinic-visit context (e.g. visit status,
// clinic conversation, video preview) and only overrides tokens that need to be different
const light_clinic: Partial<SuperclinicalTheme> = {
  backgroundFill: colors.neutral0,
};

const dark_clinic: Partial<SuperclinicalTheme> = {
  backgroundFill: colors.neutral1000,
};

export const themes = {
  light,
  dark,
  light_clinic,
  dark_clinic,
};
